import React, { useState } from 'react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../../context/AuthContext';
import { updateAnswer, deleteAnswer, acceptAnswer } from '../../api/faq';
import { createMarkdownComponents } from '../common/MarkdownComponents';

const FAQAnswerItem = ({ answer, questionAuthorId, onAnswerUpdated }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(answer.content);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isDarkMode = document.documentElement.classList.contains('dark');

  const isAuthor = user && answer.author.id === user.id;
  const isAdmin = user && (user.is_staff || user.is_superuser);
  const isQuestionAuthor = user && user.id === questionAuthorId;
  const canEdit = isAuthor || isAdmin;
  const canDelete = isAuthor || isAdmin;
  const canAccept = isQuestionAuthor || isAdmin;

  const handleEdit = () => {
    setIsEditing(true);
    setEditContent(answer.content);
    setError('');
  };

  const handleSave = async () => {
    if (!editContent.trim()) return;

    setLoading(true);
    setError('');

    try {
      await updateAnswer(answer.id, { content: editContent.trim() });
      setIsEditing(false);
      onAnswerUpdated();
    } catch (err) {
      console.error('Error updating answer:', err);
      setError(err.response?.data?.detail || 'Failed to update answer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this answer?')) return;

    setLoading(true);
    try {
      await deleteAnswer(answer.id);
      onAnswerUpdated();
    } catch (err) {
      console.error('Error deleting answer:', err);
      setError(err.response?.data?.detail || 'Failed to delete answer');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    try {
      await acceptAnswer(answer.id);
      onAnswerUpdated();
    } catch (err) {
      console.error('Error accepting answer:', err);
      setError(err.response?.data?.detail || 'Failed to accept answer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${answer.is_accepted ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-semibold">
            {answer.author.full_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {answer.author.full_name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {format(new Date(answer.created_at), 'MMM d, yyyy h:mm a')}
              {answer.updated_at !== answer.created_at && (
                <span> • Edited {format(new Date(answer.updated_at), 'MMM d, yyyy h:mm a')}</span>
              )}
            </div>
          </div>
          {answer.is_accepted && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              <svg className="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Accepted
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {canAccept && !answer.is_accepted && (
            <button
              onClick={handleAccept}
              disabled={loading}
              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
              title="Accept this answer"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleEdit}
              disabled={loading}
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
              title="Edit answer"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4h6a2 2 0 012 2v6m-3 5l-7 7H4a2 2 0 01-2-2v-4l7-7" />
              </svg>
            </button>
          )}
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
              title="Delete answer"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-md">
          {error}
        </div>
      )}

      {isEditing ? (
        <div className="space-y-4">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows="6"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editContent.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={createMarkdownComponents(isDarkMode)}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {answer.content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

const FAQAnswerList = ({ answers, questionAuthorId, onAnswerUpdated }) => {
  if (answers.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p className="text-gray-600 dark:text-gray-300">No answers yet</p>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Be the first to answer this question</p>
      </div>
    );
  }

  // Sort answers: accepted first, then by creation date
  const sortedAnswers = [...answers].sort((a, b) => {
    if (a.is_accepted && !b.is_accepted) return -1;
    if (!a.is_accepted && b.is_accepted) return 1;
    return new Date(a.created_at) - new Date(b.created_at);
  });

  return (
    <div className="space-y-4">
      {sortedAnswers.map((answer) => (
        <FAQAnswerItem
          key={answer.id}
          answer={answer}
          questionAuthorId={questionAuthorId}
          onAnswerUpdated={onAnswerUpdated}
        />
      ))}
    </div>
  );
};

export default FAQAnswerList;